import { useEffect, useRef, useState, lazy, Suspense } from 'react'
import { Link } from 'react-router-dom'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import Magnetic from './Magnetic'
// Lazy-loaded: pulls in ogl (WebGL), which is heavy enough to be worth
// keeping out of the initial bundle. It already fades in from opacity 0
// well after the hero text lands, so a deferred chunk load is unnoticeable.
const LightRays = lazy(() => import('./LightRays'))

gsap.registerPlugin(ScrollTrigger)

function SplitChars({ children, className, style }) {
  return (
    <div className={className} style={style} aria-label={String(children)}>
      {String(children).split('').map((char, i) => (
        <span
          key={i}
          className="hero-char"
          style={{ display: char === ' ' ? 'inline' : 'inline-block', willChange: 'transform, opacity' }}
        >
          {char === ' ' ? ' ' : char}
        </span>
      ))}
    </div>
  )
}

export default function Hero() {
  const heroRef      = useRef(null)
  const raysRef      = useRef(null)
  const raysInnerRef = useRef(null)
  const contentRef   = useRef(null)
  const scrollRef  = useRef(null)
  const [reduceMotion] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )

  useEffect(() => {
    const hero = heroRef.current
    if (!hero) return

    const ctx = gsap.context(() => {

      // Entrance — per-character text stagger
      gsap.set('.hero-char', { opacity: 0, y: 60 })
      gsap.to('.hero-char', {
        opacity: 1, y: 0,
        duration: 0.75,
        stagger: 0.022,
        delay: 0.35,
        ease: 'power3.out',
      })

      // Entrance — other items
      gsap.set('.hero-item', { opacity: 0, y: 36 })
      gsap.to('.hero-item', {
        opacity: 1, y: 0,
        duration: 1.1,
        stagger: 0.14,
        delay: 1.05,
        ease: 'power4.out',
      })

      // Rays fade in slowly after the text lands (inner wrapper — the outer
      // wrapper belongs to the scroll-exit tween, they must not share opacity)
      gsap.set(raysInnerRef.current, { opacity: 0 })
      gsap.to(raysInnerRef.current, { opacity: 1, duration: 2.4, delay: 0.6, ease: 'power2.inOut' })

      if (!reduceMotion) {
        gsap.to('.hero-horizon', {
          opacity: 0.85, scaleX: 1.06,
          duration: 6, yoyo: true, repeat: -1, ease: 'sine.inOut',
        })

        // Dust drifts upward and fades in loops
        gsap.set('.hero-dust', { opacity: 0 })
        gsap.to('.hero-dust', {
          opacity: () => gsap.utils.random(0.25, 0.9),
          y: () => gsap.utils.random(-60, -160),
          duration: () => gsap.utils.random(7, 15),
          repeat: -1, yoyo: true,
          ease: 'sine.inOut',
          stagger: { each: 0.45, from: 'random' },
          repeatRefresh: true,
        })
      }

      // ScrollTrigger — parallax exit as the hero scrolls away (no pin, so
      // the next section arrives without extra black scroll distance)
      const exitTl = gsap.timeline({
        scrollTrigger: {
          trigger: hero,
          start: 'top top',
          end: '+=55%',
          scrub: 0.6,
        },
      })

      exitTl
        .to(contentRef.current, { opacity: 0, y: -90, scale: 0.95, ease: 'none' }, 0)
        .to(raysRef.current,    { opacity: 0.35, ease: 'none' }, 0)
        .to(scrollRef.current,  { opacity: 0, ease: 'none' }, 0)

    }, hero)

    const refreshT = setTimeout(() => ScrollTrigger.refresh(), 200)

    return () => {
      clearTimeout(refreshT)
      ctx.revert()
    }
  }, [reduceMotion])

  return (
    <section
      ref={heroRef}
      className="hero-fullheight"
      style={{ position: 'relative', minHeight: 640, overflow: 'hidden', background: '#000' }}
    >
      {/* Volumetric light rays */}
      <div ref={raysRef} style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
        <div ref={raysInnerRef} style={{ position: 'absolute', inset: 0 }}>
        {!reduceMotion && (
          <Suspense fallback={null}>
            <LightRays
              raysOrigin="top-center"
              raysColor="#22d3ee"
              raysSpeed={1.1}
              lightSpread={0.85}
              rayLength={1.5}
              fadeDistance={1.0}
              saturation={0.9}
              followMouse
              mouseInfluence={0.14}
              noiseAmount={0.06}
              distortion={0.04}
            />
          </Suspense>
        )}
        </div>
      </div>

      {/* Fine structural grid — masked to the centre */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none',
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.024) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.024) 1px, transparent 1px)',
        backgroundSize: '72px 72px',
        WebkitMaskImage: 'radial-gradient(ellipse 62% 60% at 50% 55%, black 15%, transparent 75%)',
        maskImage: 'radial-gradient(ellipse 62% 60% at 50% 55%, black 15%, transparent 75%)',
      }} />

      {/* Floating dust specks */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none' }}>
        {[...Array(16)].map((_, i) => (
          <span key={i} className="hero-dust" style={{
            position: 'absolute',
            left: `${6 + (i * 61) % 90}%`,
            top: `${12 + (i * 37) % 72}%`,
            width: i % 3 === 0 ? 3 : 2, height: i % 3 === 0 ? 3 : 2,
            borderRadius: '50%',
            background: i % 4 === 0 ? 'rgba(34,211,238,0.55)' : 'rgba(255,255,255,0.3)',
            boxShadow: i % 4 === 0 ? '0 0 6px rgba(34,211,238,0.5)' : 'none',
            willChange: 'transform, opacity',
          }} />
        ))}
      </div>

      {/* Horizon glow — thin luminous arc low in the frame */}
      <div className="hero-horizon" style={{
        position: 'absolute', left: '50%', bottom: '14%', transform: 'translateX(-50%)',
        width: 'min(1100px, 92vw)', height: 220, zIndex: 1, pointerEvents: 'none',
        background: 'radial-gradient(ellipse 50% 34% at 50% 100%, rgba(6,182,212,0.2) 0%, rgba(6,182,212,0.045) 45%, transparent 70%)',
        opacity: 0.6, willChange: 'transform, opacity',
      }} />

      {/* Radial vignette */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 2, pointerEvents: 'none',
        background: 'radial-gradient(ellipse 85% 75% at 50% 50%, transparent 35%, rgba(0,0,0,0.5) 80%, #000 100%)',
      }} />

      {/* Bottom gradient fade into next section */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: '28%', zIndex: 3,
        background: 'linear-gradient(to bottom, transparent, #000)',
        pointerEvents: 'none',
      }} />

      {/* Hero content */}
      <div
        ref={contentRef}
        style={{
          position: 'absolute', inset: 0, zIndex: 10,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          textAlign: 'center', padding: '0 24px',
          willChange: 'transform, opacity',
        }}
      >
        {/* Brand eyebrow — the one visible line tying the "CJM Studios" name to
            what we do and where, so search engines and AI answers can tell this
            business apart from other CJM Studios */}
        <p className="hero-item hero-eyebrow" style={{
          fontSize: 12, letterSpacing: '0.2em', textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.5)', margin: '0 0 22px',
        }}>
          CJM Studios · Web design &amp; development · Brisbane
        </p>

        {/* Main headline — two visually-separate lines, but one semantic H1 */}
        <h1 style={{ margin: 0, padding: 0 }}>
          <SplitChars
            className="display-xl heading-glow"
            style={{ color: '#fff', marginBottom: 10 }}
          >
            From concept
          </SplitChars>
          <div
            className="display-xl gradient-text hero-char hero-title-gap"
            style={{ marginBottom: 40, paddingBottom: '0.1em' }}
          >
            to production.
          </div>
        </h1>

        {/* Subtext */}
        <p className="hero-item hero-sub" style={{
          fontSize: 'clamp(15px, 1.5vw, 18px)',
          color: 'rgba(255,255,255,0.5)',
          maxWidth: 520, lineHeight: 1.75,
          fontWeight: 300, marginBottom: 48,
          letterSpacing: '0.01em',
        }}>
          Websites, web apps, and software — built from scratch and
          done properly. From landing pages to full business systems,
          proven in production.
        </p>

        {/* CTAs */}
        <div className="hero-item" style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Magnetic>
            <Link to="/projects" className="btn-primary">
              <span>View our work</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </Link>
          </Magnetic>
          <Magnetic>
            <Link to="/start-a-project" className="btn-ghost">Start a website</Link>
          </Magnetic>
        </div>
        <p className="hero-item" style={{ marginTop: 22, fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>
          Got a website already?{' '}
          <Link to="/contact?service=website-check" style={{ color: '#22d3ee', textDecoration: 'none' }}>
            Get a free website check →
          </Link>
        </p>
      </div>

      <style>{`
        /* iOS Safari's address bar changes the viewport height mid-scroll,
           so a plain 100vh hero doesn't match the actual visible area and
           can look mis-sized until it settles. svh (\"small viewport height\")
           is measured against the bar-visible state, which stays stable. */
        .hero-fullheight { height: 100vh; }
        @supports (height: 100svh) {
          .hero-fullheight { height: 100svh; }
        }
        @media (max-width: 480px) {
          .hero-eyebrow { font-size: 10px !important; letter-spacing: 0.12em !important; }
        }
        @media (max-width: 360px) {
          .hero-title-gap { margin-bottom: 16px !important; }
          .hero-sub { margin-bottom: 20px !important; }
        }
      `}</style>

      {/* Scroll indicator */}
      <div
        ref={scrollRef}
        className="hero-item"
        style={{
          position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, zIndex: 10,
        }}
      >
        <span style={{ fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>
          scroll
        </span>
        <div className="scroll-pulse" style={{
          width: 1, height: 48,
          background: 'linear-gradient(to bottom, transparent, rgba(6,182,212,0.8))',
        }} />
      </div>
    </section>
  )
}
